import { createReducer, on } from '@ngrx/store';
import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { GenerationActions } from '../../actions/generations/generation.actions';
import {
  GenerationStatus,
  GenerationStepCapitals,
  GenerationStepStatus
} from 'src/app/components/generations-wizard/types';

export interface UIGeneration {
  id: string;
  name: string;
  autoPublish: boolean;
  status: GenerationStatus;
  steps: GenerationStepCapitals[];
  currentStep: GenerationStepCapitals;
  globalReadOnly: boolean;
  preserveComponentsList?: string[]; // List of component types to preserve (e.g., ["ACL_PERMISSION", "WORKFLOW"])
  [key: string]: any;
}

export interface UIGenerationStep {
  id: string;
  name: string;
  key: GenerationStepCapitals;
  status: GenerationStepStatus;
  index: number;
  error: {
    message: string;
  } | null;
  readOnly: boolean;
  aiGenerating: boolean;
  [key: string]: any;
}

interface GenerationState {
  generations: Record<string, UIGeneration>;
  currentGenerationId: string;
  generationSteps: GenerationStepState;
  underManualNavigationControl: boolean;
}

export interface GenerationStepState extends EntityState<UIGenerationStep> {}

export const generationStepAdapter = createEntityAdapter<UIGenerationStep>({
  sortComparer: (a, b) => a.index - b.index // ascending order
});

export const initialGenerationStepState: GenerationStepState = generationStepAdapter.getInitialState({
  // additional entity state properties
});

export const initialGenerationState: GenerationState = {
  generations: {},
  currentGenerationId: '',
  generationSteps: initialGenerationStepState,
  underManualNavigationControl: false
};

const generationsReducer = createReducer(
  initialGenerationState,
  on(GenerationActions.setGenerations, (state, { generations }) => {
    const generationsMap = generations.reduce(
      (acc, gen) => ({
        ...acc,
        [gen.sid]: { ...gen }
      }),
      {}
    );
    return {
      ...state,
      generations: { ...generationsMap }
    };
  }),
  on(GenerationActions.addGeneration, (state, { generation }) => {
    const generationRecord = { [generation.id]: { ...generation } };
    return {
      ...state,
      generations: { ...generationRecord, ...state.generations }
    };
  }),
  on(GenerationActions.updateGeneration, (state, { generation }) => {
    const updatedGeneration = { ...state.generations[generation.id], ...generation };
    return {
      ...state,
      generations: { ...state.generations, [generation.id]: updatedGeneration }
    };
  }),
  on(GenerationActions.setCurrentGeneration, (state, { generationId }) => {
    return {
      ...state,
      currentGenerationId: generationId
    };
  }),
  on(GenerationActions.setCurrentGenerationSteps, (state, { generationId, steps }) => {
    return {
      ...state,
      currentGenerationId: generationId,
      generationSteps: generationStepAdapter.setAll(steps, state.generationSteps)
    };
  }),
  on(GenerationActions.updateStepReadOnlyStatus, (state, { stepKey, readOnly }) => {
    return {
      ...state,
      generationSteps: generationStepAdapter.updateOne(
        {
          id: stepKey,
          changes: {
            readOnly: readOnly
          }
        },
        state.generationSteps
      )
    };
  }),
  on(GenerationActions.updatePriorStepsReadOnlyStatus, (state, { currentGenerationId, stepKey, readOnly }) => {
    const g = state.generations[currentGenerationId];
    const idx = g.steps.indexOf(stepKey as GenerationStepCapitals);
    const prevIds = g.steps.slice(0, Math.max(0, idx));
    const updates = prevIds.map((id) => ({
      id,
      changes: {
        readOnly
      }
    }));
    return {
      ...state,
      generationSteps: generationStepAdapter.updateMany(updates, state.generationSteps)
    };
  }),
  on(GenerationActions.updateAllStepsReadOnlyStatus, (state, { currentGenerationId, readOnly }) => {
    const g = state.generations[currentGenerationId];
    const updates = g.steps.map((stepId) => ({
      id: stepId,
      changes: {
        readOnly
      }
    }));
    return {
      ...state,
      generationSteps: generationStepAdapter.updateMany(updates, state.generationSteps)
    };
  }),
  on(GenerationActions.updateStepAiGeneratingStatus, (state, { stepKey, aiGenerating }) => {
    return {
      ...state,
      generationSteps: generationStepAdapter.updateOne(
        {
          id: stepKey,
          changes: {
            aiGenerating
          }
        },
        state.generationSteps
      )
    };
  }),
  on(GenerationActions.updateStep, (state, { stepKey, changes }) => {
    return {
      ...state,
      generationSteps: generationStepAdapter.updateOne(
        {
          id: stepKey,
          changes
        },
        state.generationSteps
      )
    };
  }),
  on(GenerationActions.setUnderManualNavigationControl, (state, { value }) => ({
    ...state,
    underManualNavigationControl: value
  })),
  on(GenerationActions.resetGeneration, (state) => ({
    ...state,
    currentGenerationId: '',
    generationSteps: generationStepAdapter.removeAll(state.generationSteps),
    underManualNavigationControl: false
  }))
);

export { generationsReducer, GenerationState };
